import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import PersonAddIcon from '@mui/icons-material/PersonAddAlt'
import type { PlatformUser } from '@iobiz/shared'
import { api } from '../api/client.js'
import { fetchPlatformUsers } from '../features/team/api.js'
import { VignetteFiche } from '../ui/VignetteFiche.js'

/**
 * W-36 · Console plateforme — comptes utilisateurs (§12.9).
 *
 * ⭐ À ne pas confondre avec l'écran W-30 d'une boutique : ici on gère des
 * COMPTES, globaux à la plateforme, sans rôle ni permission — un compte n'a de
 * droits que par ses appartenances, et celles-ci se règlent boutique par
 * boutique. La colonne « Boutiques » est le pont entre les deux vues.
 */

function dateCourte(iso: string | null): string {
  if (!iso) return 'Jamais'
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function PlatformUsersPage(): JSX.Element {
  const navigate = useNavigate()
  const [users, setUsers] = useState<PlatformUser[] | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [creation, setCreation] = useState(false)

  const charger = useCallback(async () => {
    setErreur(null)
    try {
      setUsers((await fetchPlatformUsers()).data)
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Chargement impossible.')
      setUsers([])
    }
  }, [])

  useEffect(() => {
    void charger()
  }, [charger])

  if (users === null) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 300 }}>
        <CircularProgress size={28} />
      </Box>
    )
  }

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', p: 6 }}>
      <Link
        component="button"
        variant="body2"
        underline="hover"
        onClick={() => navigate('/s')}
        sx={{ mb: 3, display: 'block' }}
      >
        ← Boutiques
      </Link>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, mb: 4, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Typography variant="h1">Comptes de la plateforme</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
            {users.length} compte{users.length > 1 ? 's' : ''} — les droits, eux, se règlent dans
            chaque boutique.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={() => setCreation(true)}
        >
          Nouveau compte
        </Button>
      </Box>

      {erreur && (
        <Alert severity="error" sx={{ mb: 4 }} onClose={() => setErreur(null)}>
          {erreur}
        </Alert>
      )}

      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Utilisateur</TableCell>
              <TableCell>Email</TableCell>
              <TableCell align="center">Boutiques</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell>Dernière connexion</TableCell>
              <TableCell>Créé le</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((u) => (
              <TableRow
                key={u.id}
                hover
                onClick={() => navigate(`/admin/utilisateurs/${u.id}`)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <VignetteFiche
                      images={u.avatarUrl ? [u.avatarUrl] : []}
                      repli={
                        <Typography sx={{ fontWeight: 700, fontSize: 18, color: 'text.disabled' }}>
                          {u.displayName.slice(0, 2).toUpperCase()}
                        </Typography>
                      }
                    />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {u.displayName}
                      </Typography>
                      {u.isPlatformAdmin && (
                        <Chip size="small" color="primary" label="Admin plateforme" sx={{ mt: 1 }} />
                      )}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell sx={{ color: 'text.secondary' }}>{u.email}</TableCell>
                <TableCell align="center" className="tabular">
                  {u.storeCount}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={u.status === 'active' ? 'success' : 'warning'}
                    label={u.status === 'active' ? 'Actif' : u.status}
                  />
                </TableCell>
                <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                  {dateCourte(u.lastLoginAt)}
                </TableCell>
                <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                  {dateCourte(u.createdAt)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <BoiteCreation
        open={creation}
        onClose={() => setCreation(false)}
        onCree={async () => {
          setCreation(false)
          await charger()
        }}
        onErreur={setErreur}
      />
    </Box>
  )
}

function BoiteCreation({
  open,
  onClose,
  onCree,
  onErreur,
}: {
  open: boolean
  onClose: () => void
  onCree: () => Promise<void>
  onErreur: (m: string) => void
}): JSX.Element {
  const [email, setEmail] = useState('')
  const [prenom, setPrenom] = useState('')
  const [nom, setNom] = useState('')
  const [admin, setAdmin] = useState(false)
  const [envoi, setEnvoi] = useState(false)

  useEffect(() => {
    if (open) {
      setEmail('')
      setPrenom('')
      setNom('')
      setAdmin(false)
    }
  }, [open])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Nouveau compte</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 3, pt: 2 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Une invitation part à cette adresse : la personne choisit elle-même son mot de passe.
          Rattachez ensuite le compte à une boutique depuis ses paramètres.
        </Typography>
        <TextField
          label="Adresse e-mail"
          type="email"
          value={email}
          required
          autoFocus
          onChange={(e) => setEmail(e.target.value)}
        />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField label="Prénom" value={prenom} onChange={(e) => setPrenom(e.target.value)} />
          <TextField label="Nom" value={nom} onChange={(e) => setNom(e.target.value)} />
        </Box>
        <FormControlLabel
          control={<Switch checked={admin} onChange={(e) => setAdmin(e.target.checked)} />}
          label="Administrateur de la plateforme"
        />
        {admin && (
          <Alert severity="warning">
            Un administrateur de plateforme accède à TOUTES les boutiques. Chacun de ses accès aux
            données d’une boutique est journalisé.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button
          variant="contained"
          disabled={!email.trim() || envoi}
          onClick={async () => {
            setEnvoi(true)
            try {
              const cree = await api.post<{ invitationUrl: string | null }>('/admin/users', {
                email: email.trim(),
                ...(prenom.trim() ? { firstName: prenom.trim() } : {}),
                ...(nom.trim() ? { lastName: nom.trim() } : {}),
                isPlatformAdmin: admin,
              })
              await onCree()
              if (cree.invitationUrl) {
                // Le courriel n'est pas parti : l'administrateur doit pouvoir
                // transmettre l'accès malgré tout.
                onErreur(
                  `Compte créé, mais l’invitation n’a pas pu être envoyée. Lien à transmettre : ${cree.invitationUrl}`,
                )
              }
            } catch (e) {
              onErreur(e instanceof Error ? e.message : 'Création impossible.')
            } finally {
              setEnvoi(false)
            }
          }}
        >
          Créer
        </Button>
      </DialogActions>
    </Dialog>
  )
}
